import { markRaw } from 'vue';
import { getTheme, resolveThemeName } from './themes/theme.js';
import { getObjectSchema, getAvailableEvents, isHaspProperty, getTopLevelPropertyKeys, toWireKey, fromWireFormatStates, getAllWireKeysForPart } from './v2/wireFormat.js';
import { COMMON_DEFAULTS } from './v2/shared.js';
import { getBindableProperties } from './v2/runtime.js';
import StyleResolver, { WidgetDefaultStyleSource, ThemeStyleSource, LocalStyleSource, StateStyleSource } from './v2/StyleResolver.js';
import { normalizeObjectActions } from '../../utils/objectActions.js';
import { opaWireKey, splitColorOpa } from '../../utils/color.js';
import { externalThemeToPreviewTheme } from '../../themes/themePreview.js';
import { normalizeStretchMode } from './v2/stretchMode.js';
import { normalizeGpioBindings } from '../../gpio/GpioBindingModel.js';


let _activeThemeName = 'haspdark';
let _activePrimary   = '#00aaff';
let _activeSecondary = '#ff9966';
const _externalThemes = new Map();

// Memoized so StyleResolver.style()/resolveStyleProp() can fetch "the active
// theme" on every call (needed so it's never cached/gone-stale on a node or
// the resolver — see StyleResolver.js) without rebuilding the whole
// multi-widget-type theme object on every single property lookup during a
// render/export pass. Recomputed only when the active theme selection
// actually changes; returns the SAME object reference otherwise, which is
// exactly what lets the resolver's per-part cache stay valid across a pass
// and auto-invalidate the instant the theme changes (see StyleResolver.style).
let _themeCache = null;

// Mirrors ObjectExport.js's padding→pad_top/right/left/bottom(+suffix)
// expansion in reverse, one entry per part name ObjectExport.js actually
// special-cases. The per-part expansion there is deliberately asymmetric —
// full value on main/bullet, halved on knob, left-only on item_buttons,
// "genuine firmware behaviour, not something to generalize away" per that
// file's comment — so this mirrors the exact same wire keys, not a generic
// 4-side average. A part name with no entry here was never emitted forward
// either (see ObjectExport.js), so there is nothing to fold back.
const PADDING_EXPANSION = {
  main:         { wireKeys: ['pad_top', 'pad_right', 'pad_left', 'pad_bottom'], scale: 1 },
  knob:         { wireKeys: ['pad_top20', 'pad_right20', 'pad_left20', 'pad_bottom20'], scale: 2 },
  item_buttons: { wireKeys: ['pad_left40'], scale: 1 },
  bullet:       { wireKeys: ['pad_top10', 'pad_right10', 'pad_left10', 'pad_bottom10'], scale: 1 },
}

/**
 * Reconstructs each part's single `padding` value from the pad_top/right/
 * left/bottom(+suffix) wire keys ObjectExport.js actually emits — without
 * this, `padding` (declared `exportSpecial` precisely because it changes
 * SHAPE on export, 1 value → 4 keys) never reappears on import, silently
 * reverting to its default on every Save→Load/OpenHASP-reimport. Writes the
 * same synthetic key (`padding`, `padding20`, …) fromWireFormatStates()
 * already knows how to read for that part, computed generically via
 * toWireKey — so this only has to know the VALUES' wire keys, not repeat
 * fromWireFormatStates' own key-naming logic.
 *
 * Skips any part where `config` already carries that synthetic key — a
 * widget's own constructor may have already resolved it before reaching
 * mergeConfigWithTheme (e.g. HaspChart.js excludes pad_left from its own
 * fold-back, since chart overloads it with an additive y_scale auto-pad
 * amount distinct from the uniform border padding); that always wins over
 * this generic fallback.
 */
function foldPaddingBack(schema, config) {
  for (const [partName, partDef] of Object.entries(schema.parts ?? {})) {
    if (!('padding' in (partDef.properties ?? {}))) continue
    const expansion = PADDING_EXPANSION[partName]
    if (!expansion) continue
    const syntheticKey = toWireKey(partName, 'padding', partDef, 'default')
    if (config[syntheticKey] !== undefined) continue
    const values = expansion.wireKeys.map(k => config[k]).filter(v => v !== undefined)
    if (values.length === 0) continue

    // Parts that also declare the per-side overrides (see shared.js's
    // PADDING_SIDES) keep genuinely unequal sides AS sides: their wire keys are
    // the side properties' own keys, so fromWireFormatStates imports them
    // directly and nothing has to be folded. Only a uniform set collapses back
    // into the `padding` shorthand — including a uniform 0, which must survive
    // as an explicit 0 rather than falling back to the schema default.
    if ('pad_left' in partDef.properties) {
      // Slider, Switch and Arc knobs now expose direct LVGL pad sides. Keep those sides in
      // the normalized design even when they are uniform; folding them into
      // legacy `padding` would lose the distinction between a raw LVGL pad
      // and the old total-size shorthand (padding / 2 at render time).
      if (partDef.paddingSidesAuthoritative) continue
      const uniform = values.length === expansion.wireKeys.length
        && values.every(v => Number(v) === Number(values[0]))
      if (!uniform) continue
      for (const k of expansion.wireKeys) delete config[k]
      config[syntheticKey] = Number(values[0]) * expansion.scale
      continue
    }

    // Parts declaring the AXIS-level overrides instead (shared.js's
    // PADDING_AXES — the checkbox bullet) fold their four wire keys back into
    // one value per axis: uniform across all four -> the `padding` shorthand,
    // otherwise pad_hor/pad_ver. A file whose two sides differ WITHIN an axis
    // can only have come from hand-written JSONL (the Designer never emits
    // that for such a part, and the widget cannot render it) — the larger of
    // the pair wins, same max-based reduction this function has always used.
    if ('pad_hor' in partDef.properties) {
      const axisValue = (keys) => {
        const vals = keys.map(k => config[k]).filter(v => v !== undefined).map(Number)
        return vals.length ? Math.max(...vals) : undefined
      }
      // Keys resolved BY NAME through toWireKey (pad_left -> pad_left10 for
      // bullet's '10' suffix) rather than by position in expansion.wireKeys,
      // whose order differs per part.
      const wireKey = (prop) => toWireKey(partName, prop, partDef, 'default')
      const hor = axisValue([wireKey('pad_left'), wireKey('pad_right')])
      const ver = axisValue([wireKey('pad_top'), wireKey('pad_bottom')])
      for (const k of expansion.wireKeys) delete config[k]
      if (hor !== undefined && hor === ver) {
        config[syntheticKey] = hor * expansion.scale
      } else {
        if (hor !== undefined) config[toWireKey(partName, 'pad_hor', partDef, 'default')] = hor * expansion.scale
        if (ver !== undefined) config[toWireKey(partName, 'pad_ver', partDef, 'default')] = ver * expansion.scale
      }
      continue
    }

    const pad = Math.max(...values) * expansion.scale
    if (pad > 0) config[syntheticKey] = pad
  }
}

export function setActiveTheme(name, primary, secondary) {
    _activeThemeName = _externalThemes.has(String(name))
      ? String(name)
      : resolveThemeName(name) ?? _activeThemeName;
    _activePrimary   = primary?.slice(0, 7) ?? _activePrimary;
    _activeSecondary = secondary?.slice(0, 7) ?? _activeSecondary;
    _themeCache = null;
}

/** Register a lossless library theme for synchronous canvas/style resolution. */
export function registerExternalTheme(key, text) {
    const resolvedKey = String(key);
    const previewTheme = externalThemeToPreviewTheme(text);
    _externalThemes.set(resolvedKey, previewTheme);
    if (_activeThemeName === resolvedKey) _themeCache = null;
    return previewTheme;
}

export function externalThemeKey(id) {
    return `custom:${id}`;
}

function getActiveTheme() {
    if (!_themeCache) {
        const externalTheme = _externalThemes.get(_activeThemeName);
        if (externalTheme) {
            // Keep the same small context fields exposed by bundled themes;
            // custom JSONL themes still use the design's accent colours for
            // widgets/properties that refer to them.
            externalTheme.primary_color = _activePrimary;
            externalTheme.secondary_color = _activeSecondary;
            _themeCache = externalTheme;
        } else {
            _themeCache = getTheme(_activeThemeName, _activePrimary, _activeSecondary);
        }
    }
    return _themeCache;
}

export function mergeConfigWithTheme(config) {
  config.comment ??= '';
  // GPIO is a provider-specific top-level object field. Normalize it at the
  // same import boundary as the rest of the object config, but keep the
  // diagnostics non-enumerable so editor metadata never reaches firmware
  // JSONL or design serialization.
  if (config.gpio_bindings !== undefined) {
    const gpio = normalizeGpioBindings(config.gpio_bindings);
    config.gpio_bindings = gpio.value;
    Object.defineProperty(config, '_gpioDiagnostics', {
      value: gpio.diagnostics,
      enumerable: false,
      configurable: true,
    });
  }
  // ThemeEditor previews construct real Hasp widgets without changing the
  // application's active theme. Keep that override on the node so the
  // normal StyleResolver/widget code path can consume it just like a design
  // object does. The marker is non-enumerable because Konva receives this
  // config object immediately afterwards.
  const previewTheme = config._previewTheme;
  if (previewTheme) {
    Object.defineProperty(config, '_themeOverride', {
      value: previewTheme,
      enumerable: false,
      configurable: true,
    });
    delete config._previewTheme;
  }
  const curr_theme = previewTheme ?? getActiveTheme();
  const obj_theme = previewTheme
    ? { ...(curr_theme[config.obj] ?? {}) }
    : (curr_theme[config.obj] ?? {});
  const schema = getObjectSchema(config.obj);

  // Keep the distinction between values that were present in the input and
  // values filled in below from the theme/schema.  The node model exposes
  // top-level properties as plain fields, so without this small piece of
  // provenance a later object conversion cannot tell an explicit `mode` (or
  // an explicit value equal to its default) from source-type metadata.
  if (!config._explicitTopLevelProperties) {
    const explicit = new Set();
    for (const property of getTopLevelPropertyKeys(schema ?? { properties: {} })) {
      if (config[property] !== undefined) explicit.add(property);
    }
    Object.defineProperty(config, '_explicitTopLevelProperties', {
      value: explicit,
      enumerable: false,
      configurable: true,
    });
  }

  // obj_theme may be {} for objects not in the theme (e.g. msgbox)
  config.theme = obj_theme;
  config.theme.primary_color = curr_theme.primary_color;
  config.theme.secondary_color = curr_theme.secondary_color;

  if (schema) {
    const schemaPropertyDefaults = Object.fromEntries(
      Object.entries(schema.properties ?? {})
        .filter(([, definition]) => definition?.default !== undefined)
        .map(([property, definition]) => [property, definition.default]),
    );
    // Apply top-level property defaults (imported value wins, else theme, else schema default).
    // Most theme content is style data (below), but a handful of widgets (e.g. linemeter's
    // tick_color/value_color/value_font) theme a genuine top-level property too.
    for (const key of Object.keys({ ...schema.defaults?.properties, ...schemaPropertyDefaults, ...obj_theme.properties })) {
      config[key] ??= obj_theme.properties?.[key] ?? schema.defaults?.properties?.[key] ?? schemaPropertyDefaults[key];
    }

    // Stretch modes are editor metadata, not firmware properties. Normalize
    // them at the import boundary so old projects using the former
    // `full_stretch` UI value keep their behavior and missing/invalid values
    // fall back to the stretch_mode definition's declared default.
    if (schema.properties?.stretch_mode?.options) {
      config.stretch_mode = normalizeStretchMode(schema, config.stretch_mode);
    }

    const allCaps = new Set([
      ...(schema.editorCapabilities ?? []),
      ...(schema.runtimeCapabilities ?? []),
    ]);

    // hasp_ prefixed top-level properties (angle/rotation/…) — the raw property
    // is never exposed directly on the node, only its 'hasp_' shadow.
    for (const property of Object.keys(schema.properties ?? {})) {
      if (isHaspProperty(schema, property)) {
        if (config[property] !== undefined) config['hasp_' + property] = config[property];
        else config['hasp_' + property] = schema.defaults?.properties?.[property];
        config[property] = undefined;
      }
    }
    // Mark all capability flags on config
    for (const cap of allCaps) config[cap] = true;

    // Style resolution: config.styles holds ONLY what this design file
    // actually specifies explicitly, for EVERY semantic state the schema
    // supports (not just 'default') — principle: explicit intent is
    // preserved in the editor model, see StyleResolver.js. Theme and widget
    // defaults are NOT merged in here anymore; they're resolved live, on
    // demand, by StyleResolver's WidgetDefaultStyleSource/ThemeStyleSource.
    // fromWireFormatStates is the OpenHASP import adapter's translation
    // function — this is the one legitimate place besides Importer.js where
    // flat (possibly part+state combined) wire keys are read off `config`,
    // and this call IS the state-suffix migration: any design file's
    // suffixed keys (bg_color01, bg_color1001, …) land directly in the new
    // parts.<part>.states.<state>.properties domain model, whether they came
    // from a freshly-authored file or a legacy pre-refactor export.
    foldPaddingBack(schema, config);
    config.styles = fromWireFormatStates(schema, config);
    for (const [partName, partDef] of Object.entries(schema.parts ?? {})) {
      // Delete the raw wire-keyed properties now captured in config.styles —
      // otherwise Konva's own generic setAttrs(config) (called via super(config)
      // in ObjectGroup's constructor) would create them as stray flat attrs on
      // the node, even with no explicit proxy installed for them. Every
      // state's combined wire key must be deleted, not just the default one.
      for (const wireKey of getAllWireKeysForPart(schema, partName, partDef)) {
        delete config[wireKey];
      }
    }
  }
}

export function setNodeProperties(node, config) {
  const schema = getObjectSchema(config.obj);
  if (!schema) return;

  node.config = config;
  node._explicitTopLevelProperties = new Set(config._explicitTopLevelProperties ?? []);
  node.layer = config.layer;
  node.schemaV2 = schema;
  node.obj = config.obj;
  node.comment = config.comment;
  node.haspid = config.haspid;
  node.theme = config.theme;
  node._themeOverride = config._themeOverride;

  // COMMON_DEFAULTS provide firmware defaults for opacity/click/ext_click;
  // schema.defaults.properties take precedence where defined, followed by
  // property-level schema defaults such as stretch_mode.default.
  node.defaults = { ...COMMON_DEFAULTS, ...schema.defaults?.properties };
  for (const [property, definition] of Object.entries(schema.properties ?? {})) {
    if (definition?.default !== undefined && node.defaults[property] === undefined) {
      node.defaults[property] = definition.default;
    }
  }
  node._topLevelDefaultValues = {};
  for (const property of getTopLevelPropertyKeys(schema)) {
    const themeValue = config.theme?.properties?.[property];
    if (themeValue !== undefined) node._topLevelDefaultValues[property] = themeValue;
    else if (node.defaults[property] !== undefined) node._topLevelDefaultValues[property] = node.defaults[property];
  }

  // HA bindings
  // hass_properties = the schema's bindable property set, in firmware wire
  // names — see v2/runtime.js's getBindableProperties for the migration rule:
  // a schema declaring `runtime.writable` is bound exclusively by that
  // metadata (bindings.properties may then only refine strategies, never add
  // properties); a schema without it keeps the legacy bindings.properties list.
  node.hass_properties = getBindableProperties(schema);

  node.hass_services = schema.bindings?.actions?.hass_services ?? [];
  node.hass_events   = getAvailableEvents(schema);

  // Properties: top-level (non-style) schema-derived key set only (no
  // capability flags, no part-scoped style keys — those live in node.parts,
  // read/written exclusively through node.style()/setStyle()).
  node.schemaPropertyKeys = new Set(getTopLevelPropertyKeys(schema));
  node.properties = Array.from(node.schemaPropertyKeys);

  // Capabilities from schema
  const _editorCaps  = schema.editorCapabilities  ?? [];
  const _runtimeCaps = schema.runtimeCapabilities ?? [];
  node.editorCapabilities  = new Set(_editorCaps);
  node.runtimeCapabilities = new Set(_runtimeCaps);
  [..._editorCaps, ..._runtimeCaps].forEach(cap => { node[cap] = true; });

  // Copy top-level (non-style) properties from config onto the node. Part-scoped
  // style data is never copied here — it flows exclusively through node.parts below.
  for (const key of getTopLevelPropertyKeys(schema)) {
    if (!isHaspProperty(schema, key)) {
      if (node[key] === undefined || (config.theme && key in config.theme)) {
        node[key] = config[key];
      }
    } else {
      node['hasp_' + key] = config['hasp_' + key];
    }
  }
  node.swipe = config.swipe === 1;

  // Ordered event-handler model: node.actions = [{ id, event, actions: Action[] }].
  // `config.actions` is the Designer's own structured representation, written
  // by ObjectExport and stored in design.json verbatim — the authoritative
  // source whenever present.
  //
  // `config.action` is the legacy openHASP wire map (event -> command string):
  // it appears when importing a raw plate export or a pre-package Designer
  // file, and carries no typed operands (a device never stores a `resourceId`
  // — Exporter._processActions already resolved it to `audio L:/…`). Those
  // load as plain commands, which is exactly right: the design keeps working,
  // it just has no resource reference to re-link.
  const pageId = Number(config.page ?? config.parent?.haspid);
  const resolveTarget = (targetPageId, objectId) => {
    if (targetPageId !== pageId) return null;
    const page = config.layer?.findPage?.(targetPageId);
    return page?.getChildren?.().find(item => Number(item.haspid) === objectId);
  };
  node.actions = normalizeObjectActions(config, resolveTarget);
  // `action`/`command` are no longer the action model — they are the event the
  // Action editor is currently focused on and that event's command, mirrored
  // for the editor, StatusBar and Treeview, which all read them as scalars.
  // node.actions is the single source of truth; PropertiesPanel writes through
  // to it whenever either of these changes.

  if (config.primary_color) node.primary_color = config.primary_color;
  if (config.hasp_angle !== undefined) node.hasp_angle = config.hasp_angle;
  if (node.hasProperty('rotation')) node.hasp_rotation = config.hasp_rotation;

  if (config.hass_config !== undefined) {
    node.hass_config = config.hass_config;
  }

  // Legacy GPIO event bindings were merged into node.actions by
  // normalizeObjectActions above. Keep only property-direction bindings on
  // the live provider field so the same event action cannot be edited and
  // exported twice. Runtime export reconstructs gpio_bindings.events from
  // GPIO Entity actions; the firmware contract itself remains unchanged.
  if (config.gpio_bindings !== undefined) {
    node.gpio_bindings = {
      ...config.gpio_bindings,
      events: [],
    };
    node._gpioDiagnostics = config._gpioDiagnostics ?? [];
  }

  if (config.custom_component_id)   node.custom_component_id   = config.custom_component_id;
  if (config.custom_component_name) node.custom_component_name = config.custom_component_name;

  // -------------------------------------------------------------------------
  // Normalized parts structure — node.parts is a plain, SPARSE storage object
  // (holds only explicitly-set values; see v2/StyleResolver.js) handed to the
  // resolver's LocalStyleSource/StateStyleSource, seeded from config.styles
  // (already semantic — part → prop → value, no wire keys anywhere in this
  // step, and no theme/widget defaults baked in). It is NOT public API:
  // widgets, the Property Editor and the exporter must read and write styles
  // through node.style()/node.setStyle()/node.setStateStyle(), never
  // node.parts directly.
  // -------------------------------------------------------------------------
  if (schema.parts) {
    node.parts = {};
    for (const partName of Object.keys(schema.parts)) {
      // config.styles[partName] is already { states: { default: {properties}, checked: {properties}, ... } }
      // (built by fromWireFormatStates in mergeConfigWithTheme) — no re-wrapping needed.
      node.parts[partName] = config.styles?.[partName] ?? { states: { default: { properties: {} } } };
    }

    // The resolver is a pure style engine — it takes schema metadata and an
    // ordered list of StyleSources, never the node itself. node.parts is the
    // plain storage object handed to LocalStyleSource/StateStyleSource; the
    // resolver has no idea it came from a Konva node. This is the ONLY place
    // style data lives on the node — no flat wire-keyed accessors are
    // installed. Widgets, the Property Editor, the exporter and the history
    // subsystem all read/write styles exclusively through node.style()/
    // node.styleProp()/node.setStyle()/node.setStateStyle()/node.onStyle().
    // Fixed source order — StyleResolver.resolveTrace()/hasExplicitValue()/
    // setStateStyleProp()/resetStateStyleProp() index into this array
    // positionally ([0]=widget, [1]=theme, [2]=local, [3]=state); do not
    // reorder without updating those.
    // markRaw() is essential here, not just an optimization: StyleResolver's
    // internal cache (StyleResolver.js's this._cache) stores theme object
    // REFERENCES and compares them by identity (cached.theme === theme) to
    // decide whether to reuse a cached resolved style. If Vue's reactivity
    // system ever wraps this resolver (because it hangs off `node`, and
    // `node` itself gets swept into a reactive data property elsewhere, e.g.
    // App.vue's `selectedNode`), reading a value back out of a reactive Map
    // returns a NEW proxy wrapping the same target every time — same
    // content, but never `===` to a freshly-fetched raw theme reference —
    // so the cache would never hit and, worse, writing to that reactive Map
    // during a render neither Vue nor React can safely observe read+write of
    // the same dependency in the same tick without an infinite update loop.
    // markRaw keeps the resolver (and everything reachable through it)
    // permanently outside Vue's reactivity, matching how the rest of this
    // Konva-based object model is deliberately kept non-reactive (see
    // imageStore.js/resourceUIRegistry.js for the same pattern elsewhere).
    const localSource = new LocalStyleSource(node.parts);
    const stateSource = new StateStyleSource(node.parts);
    node.styleResolver = markRaw(new StyleResolver(
      schema,
      [new WidgetDefaultStyleSource(), new ThemeStyleSource(), localSource, stateSource],
      localSource,
    ));

    // keep_circle is internal-only (never comes from JSONL/theme/defaults) —
    // initialize it once per part that declares it, from the part's
    // EFFECTIVE default-state radius (resolved through the cascade, not the
    // raw sparse storage — a design that inherits its radius from the theme
    // still needs keep_circle seeded correctly).
    for (const [partName, partDef] of Object.entries(schema.parts)) {
      if ('keep_circle' in (partDef.properties ?? {})) {
        const effectiveRadius = node.styleResolver.resolveStyleProp(partName, 'radius', { theme: config.theme });
        localSource.set(partName, 'keep_circle', effectiveRadius === 999);
        node.styleResolver.markDirty(partName); // the resolveStyleProp call above cached the pre-write value
      }
    }

    // Every hex8 style follows one Designer convention: color+opacity on the
    // firmware wire become one RGBA color in editor storage. Keep untouched
    // styles sparse, and apply the same conversion to every semantic state.
    for (const [partName, partDef] of Object.entries(schema.parts)) {
      for (const state of ['default', ...(schema.states ?? [])]) {
        const bucket = node.parts[partName]?.states?.[state]?.properties;
        if (!bucket) continue;
        for (const [colorProp, meta] of Object.entries(partDef.properties ?? {})) {
          if (meta?.type !== 'color' || meta?.format !== 'hex8') continue;
          const opacityProp = opaWireKey(colorProp);
          if (!(colorProp in bucket) && !(opacityProp in bucket)) continue;

          const rawColor = node.styleResolver.resolveStyleProp(partName, colorProp, { theme: config.theme, state }) ?? '#000000';
          const split = splitColorOpa(rawColor);
          const opacity = typeof rawColor === 'string' && rawColor.length > 7
            ? Math.round(split.opacity * 255)
            : node.styleResolver.resolveStyleProp(partName, opacityProp, { theme: config.theme, state }) ?? 255;
          const rgba = split.color.slice(0, 7) + Number(opacity).toString(16).padStart(2, '0');
          if (state === 'default') {
            localSource.set(partName, colorProp, rgba);
            if (opacityProp in (partDef.properties ?? {})) localSource.set(partName, opacityProp, opacity);
          } else {
            stateSource.set(partName, state, colorProp, rgba);
            if (opacityProp in (partDef.properties ?? {})) stateSource.set(partName, state, opacityProp, opacity);
          }
        }
      }
      node.styleResolver.markDirty(partName);
    }
  }
}

export { getActiveTheme };
